import React, { forwardRef } from 'react';
import { UserCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const AccountSection = forwardRef(({ step, isLoggedIn, currentUser, redirectToLogin }, ref) => {
  return (
    <div ref={ref} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
          <UserCircleIcon className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-base sm:text-lg font-bold text-gray-900">Account Information</h2>
          <p className="text-xs text-gray-500 hidden sm:block">Verify your account details</p>
        </div>
      </div>

      {isLoggedIn ? (
        <div className="space-y-3">
          {/* Success Message */}
          <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-green-800 text-sm">Account Verified</p>
                <p className="text-xs text-green-600">Ready to proceed</p>
              </div>
            </div>
          </div>

          {/* User Details Card */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                  {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{currentUser?.name || 'User'}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{currentUser?.email || 'No email provided'}</p>
                </div>
              </div>
              
              {currentUser?.phoneNumber && (
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 pt-2 border-t border-indigo-200">
                  <svg className="h-4 w-4 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>{currentUser.phoneNumber}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Warning Message */}
          <div className="p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border-2 border-yellow-200">
            <div className="flex items-center gap-2">
              <svg className="h-8 w-8 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-semibold text-yellow-800 text-sm">Login Required</p>
                <p className="text-xs text-yellow-600">Please log in to continue</p>
              </div>
            </div>
          </div>

          {/* Login Button */}
          <button
            onClick={() => redirectToLogin("Please login to continue with checkout")}
            className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            <UserCircleIcon className="h-4 w-4" />
            <span>Login to Continue</span>
          </button>
        </div>
      )}
    </div>
  );
});

AccountSection.displayName = 'AccountSection';

export default AccountSection;
